import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Language, SecondaryLanguage } from '../types';
import { languages } from '../data/languages';
import LanguageSelector from './LanguageSelector';
import SecondaryLanguageSelector from './SecondaryLanguageSelector';
import styles from './LanguageSetup.module.css';

function LanguageSetup(): JSX.Element {
  const [primaryLanguage, setPrimaryLanguage] = React.useState<string>('');
  const [secondaryLanguages, setSecondaryLanguages] = React.useState<SecondaryLanguage[]>([]);
  const navigate = useNavigate();

  React.useEffect(() => {
    // Load saved language selections from local storage on component mount
    const savedPrimaryLanguage = localStorage.getItem('primaryLanguage');
    const savedSecondaryLanguages = localStorage.getItem('secondaryLanguages');

    if (savedPrimaryLanguage) {
      setPrimaryLanguage(savedPrimaryLanguage);
    }
    if (savedSecondaryLanguages) {
      setSecondaryLanguages(JSON.parse(savedSecondaryLanguages));
    }
  }, []);

  const handlePrimaryLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPrimaryLanguage(e.target.value);
  };

  const handleSecondaryLanguageChange = (language: Language, proficiency: number | null) => {
    setSecondaryLanguages((prev: SecondaryLanguage[]) => {
      if (proficiency === null) {
        return prev.filter((lang: SecondaryLanguage) => lang.code !== language.code);
      }

      const existing = prev.find((lang: SecondaryLanguage) => lang.code === language.code);
      if (existing) {
        return prev.map((lang: SecondaryLanguage) => 
          lang.code === language.code ? { ...lang, proficiency } : lang
        );
      } else {
        return [...prev, { ...language, proficiency }];
      }
    });
  };

  const handleSaveAndContinue = () => {
    localStorage.setItem('primaryLanguage', primaryLanguage);
    localStorage.setItem('secondaryLanguages', JSON.stringify(secondaryLanguages));
    navigate('/translate');
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Translation App</h1>
      
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Select Primary Language</h2>
        <LanguageSelector value={primaryLanguage} onChange={handlePrimaryLanguageChange} />
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Select Secondary Languages</h2>
        <SecondaryLanguageSelector 
          secondaryLanguages={secondaryLanguages} 
          onLanguageChange={handleSecondaryLanguageChange} 
        />
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Your language selections</h2>
        <p>Primary Language: {languages.find((lang: Language) => lang.code === primaryLanguage)?.name || 'Not selected'}</p>
        <p>Secondary Languages:</p>
        <ul className={styles.languageList}>
          {secondaryLanguages.map((lang: SecondaryLanguage) => (
            <li key={lang.code} className={styles.languageItem}>
              {lang.name} - Proficiency: {lang.proficiency}/10
            </li>
          ))}
        </ul>
      </div>

      <button className={styles.button} onClick={handleSaveAndContinue}>
        Save and Continue
      </button>
    </div>
  );
}

export default LanguageSetup;
